'use client'

import { useEffect, useState } from 'react'

/**
 * "On this page" nav for the CLI technical guide with scroll-spy.
 *
 * Client-only so the parent guide can stay a server component. An
 * IntersectionObserver tracks the section ids and highlights the topmost
 * section currently inside the active band (below the sticky header, above the
 * lower third of the viewport). Clicking a link still uses the native anchor
 * jump; `scroll-mt-24` on each section keeps the target clear of the header.
 */
export function CliOnPageNav({
  items,
  label,
}: {
  items: string[][]
  /** Heading for the nav, passed down so this stays free of any copy source. */
  label: string
}) {
  const [activeId, setActiveId] = useState<string>(items[0]?.[1] ?? '')

  useEffect(() => {
    const ids = items.map(([, id]) => id)
    const elements = ids
      .map(id => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null)
    if (!elements.length) return

    const visible = new Map<string, boolean>()
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          visible.set(entry.target.id, entry.isIntersecting)
        }
        // First id in document order that is currently in the active band.
        const current = ids.find(id => visible.get(id))
        if (current) setActiveId(current)
      },
      // Top offset clears the sticky header (scroll-mt-24 = 96px); the -66%
      // bottom margin activates a section once it reaches the upper third.
      { rootMargin: '-96px 0px -66% 0px', threshold: 0 },
    )

    elements.forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [items])

  return (
    <nav className="sticky top-24 text-sm">
      <p className="text-foreground mb-3 font-medium">{label}</p>
      <ul className="space-y-2">
        {items.map(([label, id]) => {
          const active = id === activeId
          return (
            <li key={id}>
              <a
                href={`#${id}`}
                aria-current={active ? 'location' : undefined}
                className={
                  active
                    ? 'text-foreground border-primary -ms-3 block border-s-2 ps-[10px] font-medium transition-colors'
                    : 'text-muted-foreground hover:text-foreground -ms-3 block border-s-2 border-transparent ps-[10px] transition-colors'
                }
              >
                {label}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
